import {
  Alert,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Switch,
  Tabs,
  Tooltip,
  TreeSelect,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd/lib/form';
import { shipiumTheme } from '@shipium-corp/shipium-ui-theme';
import DatePicker from '~/components/dateFnsComponents/DatePicker';
import { CountryCodeDropdown } from '~/components/shared/CountryCodeDropdown';
import { ShipOptionSelect } from '~/components/printLabelTool/sections/ShipOptions/ShipOptionSelect';
import { SectionLabel } from '~/components/printLabelV2/components/StepLayout';
import { HazmatCriteriaOptions } from '~/pages/PartnerConfigPages/Rulesets/types';
import { RULE_TEST_FORM_DEFAULTS, type RuleTestFormValues } from './buildRuleTestRequest';
import type { DisabledRule } from './useRuleTestData';

const { Text } = Typography;

const inlineSectionLabelStyle: React.CSSProperties = {
  display: 'inline-block',
  marginBottom: 8,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: shipiumTheme.token.colorPrimary,
  fontWeight: 600,
};

const OptionalLabel = ({ children }: { children: React.ReactNode }) => (
  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
    {children}
    <Text type="secondary" italic style={{ fontWeight: 'normal', fontSize: 12 }}>
      Optional
    </Text>
  </span>
);

const WEIGHT_UNIT_OPTIONS: Array<{ label: string; value: 'g' | 'kg' | 'oz' | 'lb' }> = [
  { label: 'lb', value: 'lb' },
  { label: 'oz', value: 'oz' },
  { label: 'kg', value: 'kg' },
  { label: 'g', value: 'g' },
];

const HAZMAT_OPTIONS = [
  { label: 'None', value: HazmatCriteriaOptions.NONE },
  { label: 'Limited Quantity', value: HazmatCriteriaOptions.LIMITED_QUANTITY },
  { label: 'Hazmat', value: HazmatCriteriaOptions.HAZMAT },
  { label: 'Full Hazmat', value: HazmatCriteriaOptions.FULL_HAZMAT },
];

const DESTINATION_TYPE_OPTIONS = [
  { label: 'None', value: 'standard' as const },
  { label: 'PO Box', value: 'po_box' as const },
  { label: 'Highway Contract Box', value: 'hc' as const },
  { label: 'Rural Route Box', value: 'rr' as const },
];

interface CarrierTreeNode {
  title: string;
  value: string;
  key: string;
  selectable?: boolean;
  children?: Array<{ title: string; value: string; key: string }>;
}

export interface RuleTestFormProps {
  form: FormInstance<RuleTestFormValues>;
  onFinish: (values: RuleTestFormValues) => void;
  fcOptions: Array<{ value: string | undefined; label: string }>;
  fcsLoading: boolean;
  disabledRules: DisabledRule[];
  carrierTreeData: CarrierTreeNode[];
  errorMessage?: string;
}

export const RuleTestForm = ({
  form,
  onFinish,
  fcOptions,
  fcsLoading,
  disabledRules,
  carrierTreeData,
  errorMessage,
}: RuleTestFormProps) => {
  const selectedFcId = Form.useWatch('fulfillmentContextId', form);
  const exactDateDelivery = Form.useWatch('exactDateDelivery', form);

  return (
    <Form<RuleTestFormValues>
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={RULE_TEST_FORM_DEFAULTS}
      preserve
    >
      <SectionLabel label="Rules to Include" noDivider />
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: -8, marginBottom: 12 }}>
        Evaluate currently disabled rules alongside the active ruleset in this test run.
      </Text>
      <Form.Item name="inactiveRuleIdsToApply">
        <Select
          mode="multiple"
          allowClear
          placeholder={disabledRules.length === 0 ? 'No disabled rules available' : 'Select one or more disabled rules'}
          disabled={disabledRules.length === 0}
          optionLabelProp="label"
          filterOption={(input, option) => {
            const haystack = (option?.['data-search'] as string | undefined)?.toLowerCase() ?? '';
            return haystack.includes(input.toLowerCase());
          }}
        >
          {disabledRules.map((r) => (
            <Select.Option
              key={r.ruleId}
              value={r.ruleId}
              label={r.name}
              data-search={[r.position, r.name, r.description].filter(Boolean).join(' ')}
            >
              <div>
                <Text type="secondary" style={{ marginRight: 6 }}>
                  {r.position}.
                </Text>
                <Text strong>{r.name}</Text>
                {r.description && (
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                    {r.description}
                  </Text>
                )}
              </div>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Divider style={{ margin: '4px 0 16px' }} />
      <div>
        <span style={inlineSectionLabelStyle}>Test shipment</span>
        <Tooltip title="Default values shown below are used as a sample shipment. Edit any field to customize the test.">
          <InfoCircleOutlined style={{ marginLeft: 6, color: 'rgba(0,0,0,0.45)', fontSize: 12 }} />
        </Tooltip>
      </div>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: -8, marginBottom: 12 }}>
        Configure the test shipment to evaluate against the ruleset. All fields are optional unless explicitly
        indicated; default values are pre-filled.
      </Text>
      <Form.Item
        label="Fulfillment Context"
        name="fulfillmentContextId"
        rules={[{ required: true, message: 'Pick a fulfillment context' }]}
        style={{ marginBottom: 0 }}
      >
        <Select
          showSearch
          loading={fcsLoading}
          placeholder="Select a fulfillment context"
          options={fcOptions}
          optionFilterProp="label"
          listHeight={256}
        />
      </Form.Item>

      <Tabs
        defaultActiveKey="destination"
        items={[
          {
            key: 'destination',
            label: 'Destination',
            forceRender: true,
            children: (
              <>
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item label={<OptionalLabel>Country Code</OptionalLabel>} name="destinationCountry">
                      <CountryCodeDropdown allowClear />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={<OptionalLabel>Region / State</OptionalLabel>} name="destinationRegion">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={<OptionalLabel>Postal Code</OptionalLabel>} name="destinationPostalCode">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12} style={{ marginTop: -12 }}>
                  <Col span={24}>
                    <Form.Item label={<OptionalLabel>PO Box Type</OptionalLabel>} name="destinationType">
                      <Radio.Group style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
                        {DESTINATION_TYPE_OPTIONS.map((opt) => (
                          <Radio.Button
                            key={opt.value}
                            value={opt.value}
                            style={{ flex: '1 0 auto', textAlign: 'center' }}
                          >
                            {opt.label}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ),
          },
          {
            key: 'shipment',
            label: 'Shipment',
            forceRender: true,
            children: (
              <>
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item label={<OptionalLabel>Weight</OptionalLabel>} name="weightValue">
                      <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item label={<OptionalLabel>Unit</OptionalLabel>} name="weightUnit">
                      <Select options={WEIGHT_UNIT_OPTIONS} allowClear />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={<OptionalLabel>Exact-Date Delivery</OptionalLabel>}>
                      <Row gutter={8} align="middle" wrap={false}>
                        <Col>
                          <Form.Item name="exactDateDelivery" valuePropName="checked" noStyle>
                            <Switch />
                          </Form.Item>
                        </Col>
                        {exactDateDelivery && (
                          <Col flex="auto">
                            <Form.Item name="desiredDeliveryDate" noStyle>
                              <DatePicker placeholder="Desired delivery date" style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12} style={{ marginTop: -12 }}>
                  <Col span={12}>
                    <Form.Item label={<OptionalLabel>Shipment Tags</OptionalLabel>} name="shipmentTags">
                      <Select
                        mode="tags"
                        open={false}
                        tokenSeparators={[',']}
                        placeholder="Type a tag and press Enter"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={<OptionalLabel>Ship Option</OptionalLabel>} name="shipOption">
                      <ShipOptionSelect placeholder="Select a ship option" allowClear />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12} style={{ marginTop: -12 }}>
                  <Col span={24}>
                    <Form.Item
                      label={<OptionalLabel>Hazardous Material</OptionalLabel>}
                      name="hazardousCriteriaOptions"
                    >
                      <Radio.Group style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
                        {HAZMAT_OPTIONS.map((opt) => (
                          <Radio.Button
                            key={String(opt.value)}
                            value={opt.value}
                            style={{ flex: '1 0 auto', textAlign: 'center' }}
                          >
                            {opt.label}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ),
          },
          {
            key: 'tenant',
            label: 'Tenant',
            forceRender: true,
            children: (
              <Form.Item label={<OptionalLabel>Tenant ID</OptionalLabel>} name="tenantId">
                <Input placeholder="e.g. tenant-id" />
              </Form.Item>
            ),
          },
          {
            key: 'carrier',
            label: 'Carrier',
            forceRender: true,
            children: (
              <Form.Item
                label={<OptionalLabel>Carrier Service Method Allow List</OptionalLabel>}
                name="carrierServiceMethodAllowList"
              >
                <TreeSelect
                  treeCheckable
                  showCheckedStrategy="SHOW_CHILD"
                  allowClear
                  showSearch
                  style={{ width: '100%' }}
                  treeData={carrierTreeData}
                  disabled={!selectedFcId || carrierTreeData.length === 0}
                  placeholder={
                    !selectedFcId
                      ? 'Pick a fulfillment context first'
                      : carrierTreeData.length === 0
                        ? 'No carriers configured on this fulfillment context'
                        : 'Pick whole carriers or individual service methods'
                  }
                  filterTreeNode={(input, node) =>
                    (node.title?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            ),
          },
        ]}
      />

      {errorMessage ? (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Test failed" description={errorMessage} />
      ) : null}
    </Form>
  );
};
